"use client";

import { FC, useContext } from "react";
import cn from "classnames";
import { LineArrow, PauseIcon, PlayIcon } from "@/icons";
import { AutoScrollCarouselControlsContext } from "./AutoScrollCarousel";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";

export type AutoScrollCarouselControlsProps = {
  prevDld?: string;
  prevDlt?: string;
  nextDld?: string;
  nextDlt?: string;
};

export const AutoScrollCarouselControls: FC<
  AutoScrollCarouselControlsProps
> = ({ prevDld, prevDlt, nextDld, nextDlt }) => {
  const {
    goToPrevious,
    goToNext,
    isPlaying,
    canNavigate,
    pauseAutoplay,
    playAutoplay,
  } = useContext(AutoScrollCarouselControlsContext);

  const arrowButtonClassName =
    "focus-visible:outline-swdc-blue-325 -outline-offset-3 outline-none focus-visible:outline-2 focus-visible:outline-solid border-swdc-gray-100 hover:border-swdc-orange-400 active:border-swdc-orange-200 text-swdc-black hover:bg-swdc-orange-400 active:bg-swdc-orange-200 flex size-8 items-center justify-center rounded-full border bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className="flex items-center gap-4">
      {isPlaying ? (
        <button
          type="button"
          aria-label="Pause autoplay"
          className={arrowButtonClassName}
          disabled={!canNavigate}
          onClick={pauseAutoplay}
          {...getDataAutomationId("carousel-pause-button")}
        >
          <PauseIcon width={8} height={10} fill="currentColor" />
        </button>
      ) : (
        <button
          type="button"
          aria-label="Play autoplay"
          className={cn(arrowButtonClassName, "pl-0.5")}
          disabled={!canNavigate}
          onClick={playAutoplay}
          {...getDataAutomationId("carousel-play-button")}
        >
          <PlayIcon width={15} height={15} fill="currentColor" />
        </button>
      )}
      <button
        type="button"
        aria-label="Go to previous slide"
        onClick={() => {
          goToPrevious();
          pauseAutoplay();
        }}
        className={arrowButtonClassName}
        disabled={!canNavigate}
        {...getDataLinkAttributes({ dld: prevDld, dlt: prevDlt })}
        {...getDataAutomationId("carousel-back-arrow")}
      >
        <LineArrow width={15} height={15} fill="currentColor" />
      </button>
      <button
        type="button"
        aria-label="Go to next slide"
        onClick={() => {
          goToNext();
          pauseAutoplay();
        }}
        className={arrowButtonClassName}
        disabled={!canNavigate}
        {...getDataLinkAttributes({ dld: nextDld, dlt: nextDlt })}
        {...getDataAutomationId("carousel-forward-arrow")}
      >
        <LineArrow
          width={15}
          height={15}
          className="rotate-180"
          fill="currentColor"
        />
      </button>
    </div>
  );
};
